// Annual volume of a deal (operator 03.11.2026): the AI estimates, with the deal's situation, how much the customer would
// take per year per form and packaging, how certain each quantity is (firm, held out by the customer, or its own
// estimate), whether the goods are supplied by the customer (then only the packing counts), and the chance of the
// deal, with one sentence why. The code only weighs it with what an admin sets: credits per 1,000 pieces or per kg
// (meant as internal revenue, about 10 € a credit), a share per certainty and per chance. That gives 1–5 tablets, the
// full potential and the rank by expectation (credits × chance) among the open deals.

export const VOLUMEN_FORMEN = ["kapseln", "tabletten", "sticks", "pulver", "fluessig", "gummies", "sonstige"] as const;
export type VolumenForm = (typeof VOLUMEN_FORMEN)[number];

export const VOLUMEN_FORM_LABELS: Record<VolumenForm, string> = {
  kapseln: "Kapseln",
  tabletten: "Tabletten",
  sticks: "Sticks/Sachets",
  pulver: "Pulver/Granulat",
  fluessig: "Flüssig",
  gummies: "Fruchtgummis",
  sonstige: "Sonstige",
};

export const VOLUMEN_EINHEITEN = ["Stück", "kg"] as const;
export type VolumenEinheit = (typeof VOLUMEN_EINHEITEN)[number];

/**
 * How certain a quantity is (operator 03.11.2026): fest = ordered, promised or an accepted offer; in_aussicht = named by
 * the customer but not binding (inquiry, yearly demand, forecast, sales, planning figure, range); geschaetzt = the AI's
 * own assumption without a number of the customer. Estimates before then said "genannt", read as in_aussicht.
 */
export const VOLUMEN_BASEN = ["fest", "in_aussicht", "geschaetzt"] as const;
export type VolumenBasis = (typeof VOLUMEN_BASEN)[number];
export const VOLUMEN_BASIS_LABELS: Record<VolumenBasis, string> = { fest: "fest", in_aussicht: "in Aussicht", geschaetzt: "geschätzt" };
export const basisOf = (b: string): VolumenBasis => (b === "genannt" ? "in_aussicht" : (VOLUMEN_BASEN as readonly string[]).includes(b) ? (b as VolumenBasis) : "geschaetzt");

export interface VolumenPosten {
  form: VolumenForm;
  menge: number;
  einheit: VolumenEinheit;
  basis: VolumenBasis | "genannt";
  /** The customer supplies the goods (contract packing): they count only through the packaging. */
  beigestellt?: boolean;
}

/** The AI's chance that the deal comes (deals only; operator 03.11.2026). */
export const CHANCEN = ["hoch", "mittel", "niedrig"] as const;
export type Chance = (typeof CHANCEN)[number];

/** Packaging Valphira fills or packs, counted per unit and year (operator 03.11.2026): a service of its own next to the dosage form. */
export const VERPACKUNGS_ARTEN = ["blister", "dosen", "flaschen", "faltschachteln", "beutel", "sonstige"] as const;
export type VerpackungsArt = (typeof VERPACKUNGS_ARTEN)[number];

export const VERPACKUNG_LABELS: Record<VerpackungsArt, string> = {
  blister: "Blister",
  dosen: "Dosen",
  flaschen: "Flaschen",
  faltschachteln: "Faltschachteln",
  beutel: "Beutel",
  sonstige: "sonstige Packmittel",
};

export interface VerpackungsPosten {
  art: VerpackungsArt;
  menge: number;
  basis: VolumenBasis | "genannt";
}

/** Stored at the deal (deals.jahresvolumen) with its situation, and at the inquiry (inquiries.jahresvolumen). */
export interface Jahresvolumen {
  posten: VolumenPosten[];
  /** Packaging units per year; missing in estimates before the afternoon of 03.11.2026. */
  verpackung?: VerpackungsPosten[];
  begruendung: string | null;
  /** Deals only: the chance and its reason. */
  chance?: Chance;
  chanceBegruendung?: string | null;
  /** The rules the estimate was made by (PROFIL_VERSION); missing before the rules of the evening of 03.11.2026. */
  version?: number;
  at: string;
}

/** Version of the estimating rules: certainty in three steps, supplied goods, chance (operator 03.11.2026). */
export const PROFIL_VERSION = 2;

/** The AI's product type of a whole deal or inquiry (operator 03.11.2026). */
export const REGULATORIKEN = ["arzneimittel", "medizinprodukt", "nem", "sonstiges", "unklar"] as const;
export type Regulatorik = (typeof REGULATORIKEN)[number];

/** Product types of positions that are medicines or medical devices (Ange.Bot's produktart). */
const PHARMA_PRODUKTARTEN = ["pharma", "otc", "homöopathikum", "medizinprodukt"];

/**
 * Pharma, marked wherever a deal or inquiry shows (operator 03.11.2026): a medicine (prescription, OTC, homeopathic)
 * or a medical device, by the AI's judgement of the whole or by any position's product type.
 */
export function istPharma(regulatorik: string | null | undefined, produktarten: readonly string[] = []): boolean {
  return regulatorik === "arzneimittel" || regulatorik === "medizinprodukt" || produktarten.some((p) => PHARMA_PRODUKTARTEN.includes(p.trim().toLowerCase()));
}

/**
 * Credits per 1,000 pieces and per kg of each form and per 1,000 packaging units of each kind (internal revenue, about
 * 10 € a credit), the share a quantity counts with by its certainty and the deal by its chance (0 to 1), and the credits
 * from which a deal gets 2, 3, 4 and 5 tablets.
 */
export interface CreditRegeln {
  faktoren: Record<VolumenForm, { stueck: number; kg: number }>;
  verpackung: Record<VerpackungsArt, number>;
  sicherheit: Record<VolumenBasis, number>;
  chance: Record<Chance, number>;
  sterne: [number, number, number, number];
}

/** Starting values the operator adjusts in the settings: 1 credit = 1,000 capsules. */
export const DEFAULT_CREDIT_REGELN: CreditRegeln = {
  faktoren: {
    kapseln: { stueck: 1, kg: 2 },
    tabletten: { stueck: 1, kg: 2 },
    sticks: { stueck: 4, kg: 2 },
    pulver: { stueck: 1, kg: 2 },
    fluessig: { stueck: 5, kg: 2 },
    gummies: { stueck: 2, kg: 2 },
    sonstige: { stueck: 1, kg: 2 },
  },
  verpackung: { blister: 2, dosen: 3, flaschen: 3, faltschachteln: 1, beutel: 2, sonstige: 1 },
  sicherheit: { fest: 1, in_aussicht: 0.5, geschaetzt: 0.25 },
  chance: { hoch: 0.8, mittel: 0.4, niedrig: 0.1 },
  sterne: [50, 250, 1000, 5000],
};

/** The stored rules over the defaults: a form or threshold missing or not a number keeps its default. */
export function creditRegeln(stored: unknown): CreditRegeln {
  const s = (stored ?? {}) as Partial<CreditRegeln>;
  const ok = (v: unknown, d: number) => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : d);
  const faktoren = Object.fromEntries(
    VOLUMEN_FORMEN.map((f) => {
      const d = DEFAULT_CREDIT_REGELN.faktoren[f];
      const x = s.faktoren?.[f];
      return [f, { stueck: ok(x?.stueck, d.stueck), kg: ok(x?.kg, d.kg) }];
    }),
  ) as CreditRegeln["faktoren"];
  const verpackung = Object.fromEntries(VERPACKUNGS_ARTEN.map((a) => [a, ok(s.verpackung?.[a], DEFAULT_CREDIT_REGELN.verpackung[a])])) as CreditRegeln["verpackung"];
  const share = (v: unknown, d: number) => Math.min(1, ok(v, d));
  const sicherheit = Object.fromEntries(VOLUMEN_BASEN.map((b) => [b, share(s.sicherheit?.[b], DEFAULT_CREDIT_REGELN.sicherheit[b])])) as CreditRegeln["sicherheit"];
  const chance = Object.fromEntries(CHANCEN.map((c) => [c, share(s.chance?.[c], DEFAULT_CREDIT_REGELN.chance[c])])) as CreditRegeln["chance"];
  const sterne = DEFAULT_CREDIT_REGELN.sterne.map((d, i) => ok(Array.isArray(s.sterne) ? s.sterne[i] : undefined, d)) as CreditRegeln["sterne"];
  return { faktoren, verpackung, sicherheit, chance, sterne };
}

/** One line of the calculation: the full credits, the share it counts with, and whether it counts at all. */
export interface CreditTeil {
  text: string;
  /** Credits per 1,000 pieces or per kg, and which of the two. */
  faktor: number;
  je: "1.000" | "kg";
  voll: number;
  basis: VolumenBasis;
  anteil: number;
  beigestellt: boolean;
}

/** Each quantity with its credits (the base of the calculation the deal shows). */
export function creditTeile(v: Pick<Jahresvolumen, "posten" | "verpackung"> | null | undefined, r: CreditRegeln): CreditTeil[] {
  const forms = (v?.posten ?? []).map((p) => {
    const f = r.faktoren[p.form] ?? r.faktoren.sonstige;
    const kg = p.einheit === "kg";
    const faktor = kg ? f.kg : f.stueck;
    const voll = p.beigestellt ? 0 : kg ? p.menge * faktor : (p.menge / 1000) * faktor;
    const basis = basisOf(p.basis);
    return { text: `${fmtMenge(p.menge, p.einheit)} ${VOLUMEN_FORM_LABELS[p.form]}`, faktor, je: kg ? ("kg" as const) : ("1.000" as const), voll, basis, anteil: r.sicherheit[basis], beigestellt: !!p.beigestellt };
  });
  const pack = (v?.verpackung ?? []).map((p) => {
    const faktor = r.verpackung[p.art] ?? r.verpackung.sonstige;
    const basis = basisOf(p.basis);
    return { text: `${fmtMenge(p.menge, "Stück")} ${VERPACKUNG_LABELS[p.art]}`, faktor, je: "1.000" as const, voll: (p.menge / 1000) * faktor, basis, anteil: r.sicherheit[basis], beigestellt: false };
  });
  return [...forms, ...pack];
}

/** The deal's credits, each quantity weighed by its certainty; supplied goods count only through their packaging. Null without an estimate. */
export function credits(v: Pick<Jahresvolumen, "posten" | "verpackung"> | null | undefined, r: CreditRegeln): number | null {
  if (!v?.posten.length && !v?.verpackung?.length) return null;
  return creditTeile(v, r).reduce((t, x) => t + x.voll * x.anteil, 0);
}

/** The full potential: every quantity as if it were firm. */
export function potenzial(v: Pick<Jahresvolumen, "posten" | "verpackung"> | null | undefined, r: CreditRegeln): number | null {
  if (!v?.posten.length && !v?.verpackung?.length) return null;
  return creditTeile(v, r).reduce((t, x) => t + x.voll, 0);
}

/** What the deal can be expected to bring: credits times the share of its chance (medium where the AI has not judged it). */
export function erwartung(v: Pick<Jahresvolumen, "posten" | "verpackung" | "chance"> | null | undefined, r: CreditRegeln): number | null {
  const c = credits(v, r);
  return c === null ? null : c * r.chance[v?.chance ?? "mittel"];
}

/** 1 to 5 stars by the thresholds; null without credits. */
export function sterne(c: number | null, r: CreditRegeln): number | null {
  if (c === null) return null;
  return 1 + r.sterne.filter((t) => c >= t).length;
}

/** Place 1 for the most (credits or expectation); deals without have no place. Equal values share a place. */
export function volumenRang<T>(items: readonly T[], creditsOf: (t: T) => number | null): Map<T, number> {
  const ranked = items.filter((t) => creditsOf(t) !== null).sort((a, b) => creditsOf(b)! - creditsOf(a)!);
  const out = new Map<T, number>();
  ranked.forEach((t, i) => out.set(t, i > 0 && creditsOf(ranked[i - 1]!) === creditsOf(t) ? out.get(ranked[i - 1]!)! : i + 1));
  return out;
}

/** "12,5 Mio", "450.000", "7,4 t": a quantity as one reads it at a glance. */
export function fmtMenge(menge: number, einheit: VolumenEinheit): string {
  const de = (n: number, digits: number) => n.toLocaleString("de-DE", { maximumFractionDigits: digits });
  if (einheit === "kg") return menge >= 1000 ? `${de(menge / 1000, 1)} t` : `${de(menge, 0)} kg`;
  if (menge >= 1_000_000) return `${de(menge / 1_000_000, 1)} Mio`;
  return de(menge, 0);
}
